import {
  clienteListaItemSchema,
  crescimentoClienteSchema,
  motivoRejeicaoIndicacaoSchema,
} from '@varga/shared';
import { z } from 'zod';
import { api } from '../lib/http';

// ── Lista (GET /v1/clientes) ─────────────────────────────────────────────────
const listaRespostaSchema = z.object({
  total: z.number().int(),
  pagina: z.number().int(),
  limite: z.number().int(),
  itens: z.array(clienteListaItemSchema),
});
export type ClientesLista = z.infer<typeof listaRespostaSchema>;

export function listarClientes(params: { busca?: string; pagina: number; limite: number }) {
  const qs = new URLSearchParams();
  if (params.busca) qs.set('busca', params.busca);
  qs.set('pagina', String(params.pagina));
  qs.set('limite', String(params.limite));
  return api(listaRespostaSchema, `/v1/clientes?${qs.toString()}`);
}

// ── Ficha (GET /v1/clientes/:id) ─────────────────────────────────────────────
const veiculoSchema = z.object({
  id: z.string(),
  placa: z.string(),
  marca: z.string().nullable(),
  modelo: z.string().nullable(),
  ano: z.number().int().nullable(),
});

const fichaSchema = z
  .object({
    id: z.string(),
    nome: z.string(),
    telefone: z.string(),
    documento: z.string().nullable(),
    email: z.string().nullable(),
    codigoErp: z.string().nullable(),
    tipoParticipante: z.enum(['cliente', 'funcionario']),
    origem: z.string(),
    ativo: z.boolean(),
    criadoEm: z.string(),
    anonimizadoEm: z
      .union([z.string(), z.date(), z.null()])
      .transform((v) => (v == null ? null : String(v)))
      .optional(),
    codigo: z.object({ codigo: z.string() }).nullable(),
    veiculos: z.array(veiculoSchema).default([]),
    indicadoPor: z.object({ id: z.string(), nome: z.string() }).nullable(),
    crescimento: crescimentoClienteSchema,
  })
  .passthrough();
export type ClienteFicha = z.infer<typeof fichaSchema>;

export function obterCliente(id: string) {
  return api(fichaSchema, `/v1/clientes/${id}`);
}

// ── Vouchers do cliente (GET /v1/clientes/:id/vouchers) ──────────────────────
const vouchersRespostaSchema = z.object({ itens: z.array(z.unknown()) });

const voucherFrouxoSchema = z
  .object({
    id: z.string(),
    voucherCodigo: z.string(),
    papel: z.enum(['indicador', 'indicado']).nullable(),
    nivel: z.number().int().nullable(),
    descricao: z.string(),
    status: z.enum(['disponivel', 'entregue']),
    concedidoEm: z.union([z.string(), z.date()]).transform(String),
    entregueEm: z
      .union([z.string(), z.date(), z.null()])
      .transform((v) => (v == null ? null : String(v))),
    retiradoPor: z.string().nullable(),
  })
  .passthrough();
export type VoucherCliente = z.infer<typeof voucherFrouxoSchema>;

export async function listarVouchersDoCliente(id: string): Promise<VoucherCliente[]> {
  const bruto = await api(vouchersRespostaSchema, `/v1/clientes/${id}/vouchers`);
  return z.array(voucherFrouxoSchema).parse(bruto.itens);
}

// ── Indicações do cliente (GET /v1/clientes/:id/indicacoes) ──────────────────
const brindeSchema = z.object({
  papel: z.enum(['indicador', 'indicado']).nullable(),
  nivel: z.number().int().nullable(),
  descricao: z.string(),
  voucherCodigo: z.string().nullable(),
});

const indicacaoSchema = z
  .object({
    id: z.string(),
    status: z.enum(['convertida', 'rejeitada']),
    motivoRejeicao: motivoRejeicaoIndicacaoSchema.nullable().optional(),
    criadoEm: z.union([z.string(), z.date()]).transform(String),
    convertidaEm: z
      .union([z.string(), z.date(), z.null()])
      .transform((v) => (v == null ? null : String(v)))
      .optional(),
    indicado: z.object({ id: z.string(), nome: z.string() }).nullable().optional(),
    indicador: z.object({ id: z.string(), nome: z.string() }).nullable().optional(),
    ordemServico: z.object({ numeroOs: z.string() }).nullable().optional(),
    brindes: z.array(brindeSchema).default([]),
  })
  .passthrough();

const indicacoesRespostaSchema = z.object({
  feitas: z.array(indicacaoSchema).default([]),
  recebidas: z.array(indicacaoSchema).default([]),
});
export type Indicacoes = z.infer<typeof indicacoesRespostaSchema>;
export type IndicacaoItem = z.infer<typeof indicacaoSchema>;

export function obterIndicacoesDoCliente(id: string) {
  return api(indicacoesRespostaSchema, `/v1/clientes/${id}/indicacoes`);
}

// ── Anonimização (POST /v1/clientes/:id/anonimizar — LGPD Art. 18) ───────────
const anonimizarRespostaSchema = z.object({
  clienteId: z.string(),
  anonimizadoEm: z.union([z.string(), z.date()]).transform(String),
});

/** Ação irreversível, `admin` apenas (SPEC §8/§10). 409 se já anonimizado. */
export function anonimizarCliente(id: string, motivo: string) {
  return api(anonimizarRespostaSchema, `/v1/clientes/${id}/anonimizar`, {
    method: 'POST',
    body: { motivo },
  });
}
